import React from 'react';
import Select, { StylesConfig } from 'react-select';

export interface StudyOption {
  value: string;
  label: string;
  description?: string;
}

interface StudySelectProps {
  id: string;
  label: string;
  value: string;
  options: StudyOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  searchable?: boolean;
  placeholder?: string;
  describedBy?: string;
}

// Explicit colours and wrapping keep both the chosen value and open options
// readable on narrow screens and when the browser uses a dark system theme.
const styles: StylesConfig<StudyOption, false> = {
  control: (base, state) => ({ ...base, minHeight: 48, height: 'auto', backgroundColor: '#ffffff', borderColor: state.isFocused ? '#2563eb' : '#cbd5e1', borderRadius: 8, boxShadow: state.isFocused ? '0 0 0 2px #bfdbfe' : 'none', fontSize: 16, color: '#0f172a' }),
  valueContainer: base => ({ ...base, padding: '8px 12px', minWidth: 0 }),
  singleValue: base => ({ ...base, color: '#0f172a', whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', lineHeight: 1.5, overflowWrap: 'anywhere' }),
  placeholder: base => ({ ...base, color: '#475569', whiteSpace: 'normal' }),
  input: base => ({ ...base, color: '#0f172a', margin: 0, padding: 0 }),
  menu: base => ({ ...base, zIndex: 30, backgroundColor: '#ffffff', color: '#0f172a', border: '1px solid #cbd5e1', boxShadow: '0 8px 24px rgb(15 23 42 / 12%)' }),
  menuList: base => ({ ...base, padding: 4 }),
  option: (base, state) => ({ ...base, whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.5, padding: '12px', fontSize: 16, color: state.isSelected ? '#ffffff' : '#0f172a', backgroundColor: state.isSelected ? '#1d4ed8' : state.isFocused ? '#eff6ff' : '#ffffff', ':active': { backgroundColor: '#dbeafe', color: '#0f172a' } }),
  indicatorSeparator: base => ({ ...base, backgroundColor: '#cbd5e1' }),
  dropdownIndicator: base => ({ ...base, color: '#334155' }),
};

const StudySelect: React.FC<StudySelectProps> = ({ id, label, value, options, onChange, disabled, searchable = false, placeholder = 'Seçin', describedBy }) => (
  <div className="study-select-field">
    <label htmlFor={id}>{label}</label>
    <Select<StudyOption, false>
      inputId={id}
      instanceId={id}
      aria-label={label}
      aria-describedby={describedBy}
      className="study-select"
      classNamePrefix="study-select"
      styles={styles}
      value={options.find(option => option.value === value) || null}
      options={options}
      onChange={option => { if (option) onChange(option.value); }}
      isDisabled={disabled}
      isSearchable={searchable}
      isClearable={false}
      placeholder={placeholder}
      maxMenuHeight={300}
      menuPlacement="auto"
      noOptionsMessage={() => 'Eşleşen seçenek bulunamadı.'}
      loadingMessage={() => 'Seçenekler yükleniyor…'}
      formatOptionLabel={option => <div><span>{option.label}</span>{option.description && <span className="study-option-description">{option.description}</span>}</div>}
      ariaLiveMessages={{
        guidance: () => 'Listeyi açmak için aşağı oka basın. Ok tuşlarıyla ilerleyin, Enter ile seçin, Escape ile kapatın.',
        onChange: ({ label: optionLabel }) => `${optionLabel || 'Seçenek'} seçildi.`,
        onFocus: ({ label: optionLabel, isSelected }) => `${optionLabel || 'Seçenek'}${isSelected ? ', seçili' : ''}.`,
        onFilter: ({ resultsMessage }) => resultsMessage,
      }}
      screenReaderStatus={({ count }) => `${count} seçenek bulundu.`}
    />
  </div>
);

export default StudySelect;
